export const API_ERROR_MESSAGES: Record<string, string> = {
  FEATURE_MAPPED_TO_PLANS:
    'This feature is mapped to an active plan and cannot be deactivated or terminated.',
  PLAN_HAS_ACTIVE_SUBSCRIPTIONS:
    'This plan has active subscriptions and cannot be deleted or deactivated.',
  PLAN_MAPPED_TO_FEATURES: 'This plan cannot be updated because it is linked to active mappings.',
  VALIDATION_FAILED: 'Please check the form and correct the highlighted fields.',
  RESOURCE_NOT_FOUND: 'The requested record was not found.',
  UNAUTHORIZED_ACCESS: 'You are not authorized to perform this action.',
  DUPLICATE_RECORD: 'A record with the same details already exists.',
  EMAIL_EXISTS: 'A user with this email address already exists.',
  MOBILE_NUMBER_EXISTS: 'A user with this mobile number already exists.',
  UNKNOWN_ERROR: 'Something went wrong. Please try again.',
  UNHANDLED_EXCEPTION: 'An unexpected error occurred. Please try again.',
  HTTP_400: 'The request was invalid. Please review your input and try again.',
  HTTP_401: 'Your session has expired. Please sign in again.',
  HTTP_403: 'You do not have permission to perform this action.',
  HTTP_404: 'The requested resource was not found.',
  HTTP_409: 'This action conflicts with existing data.',
  HTTP_500: 'Server error. Please try again later.',
};

export const API_ERROR_FALLBACKS = {
  GENERIC: 'Something went wrong. Please try again.',
  LOAD_FEATURES: 'Failed to load features. Please try again.',
  LOAD_FEATURE: 'Failed to load feature details.',
  CREATE_FEATURE: 'Feature creation failed. Please try again.',
  UPDATE_FEATURE: 'Feature update failed. Please try again.',
  DELETE_FEATURE: 'Failed to delete feature. Please try again.',
  UPDATE_FEATURE_STATUS: 'Failed to update feature status. Please try again.',
  LOAD_PLANS: 'Failed to load plans. Please try again.',
  LOAD_PLAN: 'Failed to load plan details.',
  CREATE_PLAN: 'Plan creation failed. Please try again.',
  UPDATE_PLAN: 'Plan update failed. Please try again.',
  DELETE_PLAN: 'Failed to delete plan. Please try again.',
  UPDATE_PLAN_STATUS: 'Failed to update plan status. Please try again.',
  LOAD_LEADS: 'Failed to load lead capture responses. Please try again.',
  LOAD_INCOMPLETE_ENTERPRISES: 'Failed to load incomplete enterprises. Please try again.',
  LOAD_ENTERPRISES: 'Failed to load enterprises. Please try again.',
  LOAD_ENTERPRISE: 'Failed to load enterprise details.',
  CREATE_ENTERPRISE_USER: 'Failed to register enterprise admin. Please try again.',
  VERIFY_OTP: 'OTP verification failed. Please try again.',
  CREATE_ORGANIZATION: 'Failed to create organization. Please try again.',
  UPDATE_ORGANIZATION: 'Failed to update organization. Please try again.',
  UPDATE_USER: 'Failed to update user profile. Please try again.',
  CREATE_SUBSCRIPTION: 'Failed to create subscription. Please try again.',
  LOAD_ORDER_SUMMARY: 'Failed to load order summary. Please try again.',
  CAPTURE_PAYMENT: 'Failed to capture payment. Please try again.',
  ACTIVATE_SUBSCRIPTION: 'Failed to activate subscription. Please try again.',
  LOAD_TEMPLATES: 'Failed to load templates. Please try again.',
  LOAD_TEMPLATE: 'Failed to load template details.',
  CREATE_TEMPLATE: 'Template creation failed. Please try again.',
  UPDATE_TEMPLATE: 'Template update failed. Please try again.',
  DELETE_TEMPLATE: 'Failed to delete template. Please try again.',
} as const;

export type ApiErrorFallbackKey = keyof typeof API_ERROR_FALLBACKS;

export const API_SUCCESS_MESSAGES = {
  FEATURE_CREATED: 'Feature created successfully.',
  FEATURE_UPDATED: 'Feature updated successfully.',
  FEATURE_DELETED: 'Feature deleted successfully.',
  FEATURE_STATUS_UPDATED: 'Feature status updated successfully.',
  PLAN_CREATED: 'Plan created successfully.',
  PLAN_UPDATED: 'Plan updated successfully.',
  PLAN_DELETED: 'Plan deleted successfully.',
  PLAN_STATUS_UPDATED: 'Plan status updated successfully.',
  TEMPLATE_CREATED: 'Template created successfully.',
  TEMPLATE_UPDATED: 'Template updated successfully.',
  TEMPLATE_DELETED: 'Template deleted successfully.',
  ENTERPRISE_USER_CREATED: 'Enterprise admin registered. Verify OTP to continue.',
  OTP_RESENT: 'Verification code resent successfully.',
  ORGANIZATION_CREATED: 'Organization created successfully.',
  ORGANIZATION_UPDATED: 'Organization updated successfully.',
  USER_UPDATED: 'User profile updated successfully.',
  SUBSCRIPTION_CREATED: 'Subscription created successfully.',
  PAYMENT_CAPTURED: 'Payment captured successfully.',
  SUBSCRIPTION_ACTIVATED: 'Subscription activated successfully.',
  ONBOARDING_COMPLETE: 'Enterprise onboarding completed successfully.',
} as const;

export type ApiSuccessMessageKey = keyof typeof API_SUCCESS_MESSAGES;
